import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Rating,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import { useDispatch, useSelector } from 'react-redux';
import { saveBrandReview } from '../../redux/buzzbridge/actions';
import { brandReviewSaveErrorSelector, savingBrandReviewSelector } from '../../redux/buzzbridge/selectors';
import { FONT_ENUM } from '../../types/global/fonts.types';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import {
    BRAND_LUXE_BG,
    BRAND_LUXE_PRIMARY,
    BRAND_MODAL_ACCENT,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../../pages/BrandDashboard/brandDashboardTokens';

const MAX_REVIEW_CHARS = 2000;
const STAR_SX = {
    color: BRAND_MODAL_ACCENT,
    '& .MuiRating-iconFilled': { color: BRAND_LUXE_PRIMARY },
    '& .MuiRating-iconHover': { color: BRAND_LUXE_PRIMARY },
    '& .MuiRating-iconEmpty': { color: 'rgba(255,255,255,0.25)' },
};

export interface BrandReviewModalProps {
    open: boolean;
    onClose: () => void;
    brandId: string;
    brandName?: string;
    brandProfilePicture?: string;
    mode: 'create' | 'edit';
    initialRating?: number;
    initialReviewText?: string;
    reviewId?: string;
    suggestedCampaignParticipant?: string | null;
    participantId?: string;
}

function parseSaveError(error: unknown): string | null {
    const err = error as { data?: { message?: string }; message?: string; response?: { data?: { message?: string } } };
    return err?.response?.data?.message ?? err?.data?.message ?? err?.message ?? null;
}

export default function BrandReviewModal({
    open,
    onClose,
    brandId,
    brandName,
    brandProfilePicture,
    mode,
    initialRating = 0,
    initialReviewText = '',
    reviewId,
    suggestedCampaignParticipant,
    participantId,
}: BrandReviewModalProps) {
    const dispatch = useDispatch();
    const saving = useSelector(savingBrandReviewSelector);
    const saveError = useSelector(brandReviewSaveErrorSelector);

    const [rating, setRating] = useState<number | null>(null);
    const [reviewText, setReviewText] = useState('');

    useEffect(() => {
        if (!open) return;
        setRating(initialRating > 0 ? initialRating : null);
        setReviewText(initialReviewText ?? '');
    }, [open, initialRating, initialReviewText]);

    const title = mode === 'edit' ? 'Update your review' : 'Rate this brand';
    const subtitle = useMemo(() => {
        if (mode === 'edit') {
            return 'Update your star rating or feedback. You can only leave one review per brand.';
        }
        return 'Share how your collaboration went. Only influencers with a completed campaign can review.';
    }, [mode]);

    const panelErr = parseSaveError(saveError);
    const canSubmit = Boolean(rating && rating >= 1 && rating <= 5 && !saving);

    const handleSubmit = () => {
        if (!canSubmit || !rating) return;
        dispatch(
            saveBrandReview({
                brandId,
                rating,
                review_text: reviewText.trim() || undefined,
                reviewId: mode === 'edit' ? reviewId : undefined,
                campaign_participant:
                    mode === 'create' && suggestedCampaignParticipant ? suggestedCampaignParticipant : undefined,
                participantId,
            }),
        );
    };

    const wasSavingRef = useRef(false);
    useEffect(() => {
        if (wasSavingRef.current && !saving && !saveError && open) {
            onClose();
        }
        wasSavingRef.current = saving;
    }, [saving, saveError, open, onClose]);

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                    backgroundImage: 'none',
                    borderRadius: 3,
                    border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                },
            }}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    color: '#fff',
                    pb: 1,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                    {brandProfilePicture ? (
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                overflow: 'hidden',
                                border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                flexShrink: 0,
                            }}>
                            <CustomLazyLoadImage
                                src={brandProfilePicture}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        </Box>
                    ) : null}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, lineHeight: 1.3 }}>
                            {title}
                        </Typography>
                        {brandName ? (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 13,
                                    color: BRAND_MODAL_ACCENT,
                                    mt: 0.25,
                                }}
                                noWrap>
                                {brandName}
                            </Typography>
                        ) : null}
                    </Box>
                </Box>
                <IconButton onClick={onClose} disabled={saving} size="small" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 0 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 14,
                        color: 'rgba(255,255,255,0.72)',
                        lineHeight: 1.6,
                        mb: 2.5,
                    }}>
                    {subtitle}
                </Typography>

                <Box
                    sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                        bgcolor: BRAND_SURFACE_CONTAINER,
                        mb: 2,
                    }}>
                    <Typography
                        sx={{
                            fontSize: 10,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            color: 'rgba(255,255,255,0.45)',
                            mb: 1,
                        }}>
                        Your rating
                    </Typography>
                    <Rating
                        value={rating}
                        onChange={(_, v) => setRating(v)}
                        size="large"
                        icon={<StarIcon fontSize="inherit" />}
                        emptyIcon={<StarBorderIcon fontSize="inherit" />}
                        sx={{ ...STAR_SX, fontSize: 36 }}
                    />
                    {rating ? (
                        <Typography
                            sx={{ mt: 1, fontSize: 13, color: BRAND_LUXE_PRIMARY, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            {rating} of 5 stars
                        </Typography>
                    ) : (
                        <Typography sx={{ mt: 1, fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>
                            Tap a star to rate
                        </Typography>
                    )}
                </Box>

                <TextField
                    label="Written review (optional)"
                    placeholder="What went well? What could improve?"
                    value={reviewText}
                    onChange={e => setReviewText(e.target.value.slice(0, MAX_REVIEW_CHARS))}
                    multiline
                    minRows={4}
                    fullWidth
                    helperText={`${reviewText.length}/${MAX_REVIEW_CHARS}`}
                    InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.55)' } }}
                    FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)', textAlign: 'right' } }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: '#fff',
                            bgcolor: BRAND_LUXE_BG,
                            borderRadius: 2,
                            '& fieldset': { borderColor: BRAND_OUTLINE_VARIANT },
                            '&:hover fieldset': { borderColor: 'rgba(255,201,101,0.35)' },
                            '&.Mui-focused fieldset': { borderColor: BRAND_MODAL_ACCENT },
                        },
                    }}
                />

                {panelErr ? (
                    <Typography sx={{ mt: 2, fontSize: 13, color: '#ff7351', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                        {panelErr}
                    </Typography>
                ) : null}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
                <Button
                    onClick={onClose}
                    disabled={saving}
                    sx={{
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: 'rgba(255,255,255,0.6)',
                    }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    sx={{
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        px: 3,
                        borderRadius: 999,
                        bgcolor: BRAND_LUXE_PRIMARY,
                        border: `1px solid ${BRAND_LUXE_PRIMARY}`,
                        color: '#131517',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: BRAND_LUXE_PRIMARY, boxShadow: 'none' },
                        '&.Mui-disabled': { bgcolor: 'rgba(255,201,101,0.25)', color: 'rgba(0,0,0,0.35)' },
                    }}>
                    {saving && <CircularProgress size={18} sx={{ color: '#131517', mr: 1 }} />}
                    {mode === 'edit' ? 'Save changes' : 'Submit review'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
